/**
 * Lo que el cliente le dice a su gestoría — `OD-0067` D-02 y D-03 (ACCEPTED
 * 2026-10-28), plan Business (feature `accountant_export`).
 *
 * ```text
 * TarjetaNotaAsesoria       en la ficha de una factura EMITIDA o de una compra
 *                           anotada: una etiqueta y una nota, que salen en las
 *                           dos ultimas columnas del Excel y el CSV
 * ComentarioDelTrimestre    en el panel de la exportacion, con el trimestre
 *                           elegido: va en una hoja del Excel y en el ZIP
 * ```
 *
 * ## Lo que esta pantalla NO decide
 *
 * - **Ni quién escribe**: `puedeEscribir` llega de la API por el rol. Quien no
 *   puede (SALES, solo lectura, la gestoría) la ve sin poder cambiarla.
 * - **Ni si el plan la incluye**: fuera de Business la API responde 402 y la
 *   tarjeta no se pinta. No es una función que se eche de menos en cada
 *   factura, y `OD-0067` D-02 dice que tras bajar de plan se deja de enseñar.
 * - **Ni las longitudes**: se las dice la API al guardar, campo a campo.
 *
 * La factura no se toca: la nota vive aparte (`tenant/0046`).
 */
import { useCallback, useEffect, useState } from "react";

import { api, ESTADO_VALIDACION, type ComentarioDelTrimestre as Comentario, type ErrorApi, type NotaParaLaAsesoria } from "../api/cliente";
import { t } from "../i18n/es";
import { diaLegible } from "../ui/formato";
import { AreaTexto, Aviso, Boton, Campo, Tarjeta } from "../ui/primitivas";

type Mensaje = { readonly tono: "exito" | "error"; readonly texto: string };

/** El día de Madrid de un instante ISO, para «última vez cambiada el …». */
function dia(iso: string): string {
  return diaLegible(new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Madrid" }).format(new Date(iso)));
}

function errorDe(campos: Readonly<Record<string, string>>, campo: string): string | null {
  const c = campos[campo];
  return c === undefined ? null : (t.notaAsesoria.errores[c] ?? t.comun.errorGenerico);
}

export function TarjetaNotaAsesoria({
  documento,
  id,
  hayConexion,
  onSesionCaducada,
}: {
  readonly documento: "factura" | "compra";
  readonly id: string;
  readonly hayConexion: boolean;
  readonly onSesionCaducada: () => void;
}) {
  const [ficha, setFicha] = useState<NotaParaLaAsesoria | null>(null);
  const [etiqueta, setEtiqueta] = useState("");
  const [nota, setNota] = useState("");
  const [campos, setCampos] = useState<Readonly<Record<string, string>>>({});
  const [mensaje, setMensaje] = useState<Mensaje | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const mostrar = (n: NotaParaLaAsesoria): void => {
    setFicha(n);
    setEtiqueta(n.etiqueta ?? "");
    setNota(n.nota ?? "");
  };

  const cargar = useCallback(async (): Promise<void> => {
    const r = documento === "factura" ? await api.notaDeFactura(id) : await api.notaDeCompra(id);
    if (r.ok) return mostrar(r.datos);
    if (r.estado === 401) onSesionCaducada();
    // Sin el plan, sin permiso o sin red: la ficha sigue a lo suyo sin la tarjeta.
  }, [documento, id, onSesionCaducada]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  if (ficha === null) return null;

  async function guardar(): Promise<void> {
    setOcupado(true);
    setCampos({});
    setMensaje(null);
    const datos = { etiqueta, nota };
    const r = documento === "factura" ? await api.guardarNotaDeFactura(id, datos) : await api.guardarNotaDeCompra(id, datos);
    setOcupado(false);
    if (!r.ok) return rechazo(r);
    mostrar(r.datos);
    setMensaje({ tono: "exito", texto: r.datos.nota === null && r.datos.etiqueta === null ? t.notaAsesoria.borrada : t.notaAsesoria.guardada });
  }

  function rechazo(r: { readonly estado: number; readonly error: ErrorApi | null }): void {
    if (r.estado === 401) return onSesionCaducada();
    if (r.estado === ESTADO_VALIDACION && r.error !== null) return setCampos(r.error.campos);
    setMensaje({ tono: "error", texto: t.notaAsesoria.error });
  }

  return (
    <Tarjeta titulo={t.notaAsesoria.titulo}>
      {ficha.puedeEscribir ? (
        <>
          <p className="ayuda-neutra">{t.notaAsesoria.explicacion}</p>
          <Campo
            id={`nota-asesoria-etiqueta-${id}`}
            etiqueta={t.notaAsesoria.etiqueta}
            valor={etiqueta}
            onChange={setEtiqueta}
            ayuda={t.notaAsesoria.ayudaEtiqueta}
            error={errorDe(campos, "etiqueta")}
          />
          <AreaTexto
            id={`nota-asesoria-nota-${id}`}
            etiqueta={t.notaAsesoria.nota}
            valor={nota}
            onChange={setNota}
            filas={3}
            ayuda={t.notaAsesoria.ayudaNota}
            error={errorDe(campos, "nota")}
          />
          <div className="acciones">
            <Boton onClick={() => void guardar()} deshabilitado={!hayConexion || ocupado}>
              {ocupado ? t.notaAsesoria.guardando : t.notaAsesoria.guardar}
            </Boton>
          </div>
        </>
      ) : ficha.nota === null && ficha.etiqueta === null ? (
        <p>{t.notaAsesoria.sinNota}</p>
      ) : (
        <dl className="nota-asesoria-lectura">
          {ficha.etiqueta !== null ? (
            <>
              <dt>{t.notaAsesoria.etiqueta}</dt>
              <dd>{ficha.etiqueta}</dd>
            </>
          ) : null}
          {ficha.nota !== null ? (
            <>
              <dt>{t.notaAsesoria.nota}</dt>
              <dd className="texto-con-saltos">{ficha.nota}</dd>
            </>
          ) : null}
        </dl>
      )}
      {!ficha.puedeEscribir ? <p className="ayuda-neutra">{t.notaAsesoria.soloLectura}</p> : null}
      {ficha.actualizadaEn !== null ? <p className="ayuda-neutra">{t.notaAsesoria.actualizada(dia(ficha.actualizadaEn))}</p> : null}
      {mensaje !== null ? <Aviso tono={mensaje.tono}>{mensaje.texto}</Aviso> : null}
    </Tarjeta>
  );
}

/**
 * D-03: el comentario de UN trimestre, dentro del panel de la exportación.
 * Se monta con `key={trimestre}`: al cambiar de trimestre se lee el suyo.
 */
export function ComentarioDelTrimestre({
  trimestre,
  nombre,
  hayConexion,
}: {
  readonly trimestre: string;
  /** Cómo se lee en pantalla: «3.er trimestre de 2026». */
  readonly nombre: string;
  readonly hayConexion: boolean;
}) {
  const [ficha, setFicha] = useState<Comentario | null>(null);
  const [texto, setTexto] = useState("");
  const [campos, setCampos] = useState<Readonly<Record<string, string>>>({});
  const [mensaje, setMensaje] = useState<Mensaje | null>(null);
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    let vivo = true;
    void api.comentarioDelTrimestre(trimestre).then((r) => {
      if (!vivo || !r.ok) return;
      setFicha(r.datos);
      setTexto(r.datos.comentario ?? "");
    });
    return () => {
      vivo = false;
    };
  }, [trimestre]);

  // Quien no puede escribirlo y no hay nada escrito: nada que enseñar.
  if (ficha === null || (!ficha.puedeEscribir && ficha.comentario === null)) return null;

  async function guardar(): Promise<void> {
    setOcupado(true);
    setCampos({});
    setMensaje(null);
    const r = await api.guardarComentarioDelTrimestre(trimestre, texto);
    setOcupado(false);
    if (!r.ok) {
      if (r.estado === ESTADO_VALIDACION && r.error !== null) return setCampos(r.error.campos);
      return setMensaje({ tono: "error", texto: t.notaAsesoria.error });
    }
    setFicha(r.datos);
    setTexto(r.datos.comentario ?? "");
    setMensaje({ tono: "exito", texto: r.datos.comentario === null ? t.notaAsesoria.comentarioBorrado : t.notaAsesoria.comentarioGuardado });
  }

  return (
    <div className="exportacion-bloque">
      <h4>{t.notaAsesoria.comentarioTitulo}</h4>
      {ficha.puedeEscribir ? (
        <>
          <AreaTexto
            id="exportacion-comentario"
            etiqueta={t.notaAsesoria.comentarioEtiqueta}
            valor={texto}
            onChange={setTexto}
            filas={3}
            ayuda={t.notaAsesoria.comentarioAyuda(nombre)}
            error={errorDe(campos, "comentario")}
          />
          <div className="acciones">
            <Boton variante="secundario" onClick={() => void guardar()} deshabilitado={!hayConexion || ocupado}>
              {ocupado ? t.notaAsesoria.guardando : t.notaAsesoria.guardarComentario}
            </Boton>
          </div>
        </>
      ) : (
        <p className="texto-con-saltos">{ficha.comentario}</p>
      )}
      {mensaje !== null ? <Aviso tono={mensaje.tono}>{mensaje.texto}</Aviso> : null}
    </div>
  );
}
